import type { IAiScriptChatCellContext } from "@/payroll/ai-script/types";
import {
  findReferenced,
  recordId,
  type RecordObject,
  type RecordRef,
  recordKeyColumns,
  recordTitle,
} from "@/profitability/records/objects";
import type {
  ReportColumn,
  ReportDataset,
  ReportOutput,
  ReportRow,
} from "@/profitability/types";
import { isSourced } from "./sources/sourced";

export interface WorkbookCellRecord {
  ref: RecordRef;
  label: string;
}

// The sheet names the API gives a generated workbook
// (`Profitability::ReportScript#sheet_name`): a dataset's title, else its id
// humanized, cleaned the way Excel needs, deduplicated past the Method sheet.
const METHOD_SHEET = "Method";
const MAX_SHEET_NAME = 31;
const FORBIDDEN_IN_SHEET_NAME = /[:\\/?*[\]]/g;

const cleanSheetName = (value: string) =>
  value
    .replace(FORBIDDEN_IN_SHEET_NAME, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_SHEET_NAME);

const humanize = (id: string) => {
  const words = id.replace(/_id$/, "").replace(/_/g, " ").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * The datasets the workbook has a sheet for: a detail dataset is written
 * only to the download, so it takes no sheet name here either.
 */
const sheetDatasets = (output: ReportOutput | undefined) =>
  (output?.datasets ?? []).filter((dataset) => !dataset.detail);

const sheetNames = (datasets: ReportDataset[]) => {
  const used = [METHOD_SHEET];
  return datasets.map((dataset) => {
    const base =
      cleanSheetName(dataset.title ?? "") ||
      cleanSheetName(humanize(dataset.id)) ||
      "Sheet";
    let name = base;
    for (let suffix = 2; used.includes(name) && suffix < 100; suffix += 1) {
      const room = MAX_SHEET_NAME - String(suffix).length - 1;
      name = `${base.slice(0, room)} ${suffix}`;
    }
    if (used.includes(name)) name = base;
    used.push(name);
    return name;
  });
};

const columnHeader = (column: ReportColumn) =>
  column.label || column.key.replace(/_/g, " ");

const valueOf = (row: ReportRow, column: ReportColumn) =>
  String(row[column.key] ?? "");

/**
 * The row a cell of a dataset's own sheet sits on: the sheet's row (row 1
 * is the header) when its value still matches, else the first row with it.
 */
const rowAt = (
  dataset: ReportDataset,
  column: ReportColumn,
  coordinate: string,
  value: string,
) => {
  const sheetRow = Number(/\d+$/.exec(coordinate)?.[0] ?? 0);
  const placed = sheetRow > 1 ? dataset.rows[sheetRow - 2] : undefined;
  if (placed && valueOf(placed, column) === value) return placed;
  return dataset.rows.find((row) => valueOf(row, column) === value);
};

const cellRecord = (object: RecordObject, row: ReportRow) => ({
  ref: { dataset: object.dataset.id, recordId: recordId(object.dataset, row) },
  label: recordTitle(object, row),
});

/**
 * Reads a generated workbook's cells as the records they name. A cell names
 * a record when its sheet is a dataset's and its column either references
 * another dataset's records (a job's technician) or is the column that
 * names or keys the sheet's own records (a technician's name, a job's
 * number). Anything else (a total, a figure, the Method sheet) names none.
 */
export const workbookCellRecords = (
  output: ReportOutput | undefined,
  objects: RecordObject[],
) => {
  const datasets = sheetDatasets(output);
  const names = sheetNames(datasets);
  const datasetOf = (sheetName: string) =>
    datasets.find(
      (dataset, index) =>
        names[index] === sheetName ||
        dataset.title === sheetName ||
        dataset.id === sheetName,
    );

  return (cell: IAiScriptChatCellContext): WorkbookCellRecord | null => {
    const value = cell.value?.trim();
    if (!value || !cell.columnHeader) return null;
    const dataset = datasetOf(cell.sheetName);
    const column = dataset?.columns.find(
      (item) => columnHeader(item) === cell.columnHeader,
    );
    if (!dataset || !column) return null;

    if (column.references) {
      const target = objects.find(
        (object) => object.dataset.id === column.references,
      );
      const row = target && findReferenced(target, value);
      return target && row ? cellRecord(target, row) : null;
    }

    const object = objects.find((item) => item.dataset.id === dataset.id);
    const namesRecord =
      object &&
      (column.key === object.nameColumn.key ||
        recordKeyColumns(dataset).includes(column.key));
    if (!object || !namesRecord) return null;
    const row = rowAt(dataset, column, cell.coordinate, value);
    return row ? cellRecord(object, row) : null;
  };
};

export interface WorkbookCellSource {
  object: RecordObject;
  row: ReportRow;
  /** The figure's column key; null for the record as a whole. */
  column: string | null;
}

/**
 * Reads a generated workbook's cells as the figures they show: a cell of a
 * record dataset's own sheet, in a column with a `source` or `traced` (that figure), or
 * in the column that names the sheet's records (the record as a whole). The
 * cell's row is the sheet's row when its first column still names the same
 * record, else the row its first column names.
 */
export const workbookCellSources = (
  output: ReportOutput | undefined,
  objects: RecordObject[],
) => {
  const datasets = sheetDatasets(output);
  const names = sheetNames(datasets);
  return (cell: IAiScriptChatCellContext): WorkbookCellSource | null => {
    if (!cell.columnHeader) return null;
    const index = datasets.findIndex(
      (dataset, position) =>
        names[position] === cell.sheetName ||
        dataset.title === cell.sheetName ||
        dataset.id === cell.sheetName,
    );
    const object = objects.find(
      (item) => item.dataset.id === datasets[index]?.id,
    );
    const column = object?.dataset.columns.find(
      (item) => columnHeader(item) === cell.columnHeader,
    );
    if (!object || !column) return null;
    const { dataset } = object;
    const namesRecord =
      column.key === object.nameColumn.key ||
      recordKeyColumns(dataset).includes(column.key);
    const sourced = isSourced(dataset, column);
    if (!sourced && !namesRecord) return null;
    const first = dataset.columns[0];
    // A number in the first column may show with thousands separators.
    const plain = (text: string) => text.replace(/,/g, "").trim();
    const label = cell.rowLabel ? plain(cell.rowLabel) : "";
    const sheetRow = Number(/\d+$/.exec(cell.coordinate)?.[0] ?? 0);
    const placed = sheetRow > 1 ? dataset.rows[sheetRow - 2] : undefined;
    const row =
      placed && (!label || !first || plain(valueOf(placed, first)) === label)
        ? placed
        : label && first
          ? dataset.rows.find((item) => plain(valueOf(item, first)) === label)
          : undefined;
    if (!row) return null;
    return { object, row, column: sourced ? column.key : null };
  };
};
